import React, { useEffect, useState } from 'react';

// Utility to check if cookies are allowed for non-authenticated users
export function isVisitorCookiesAllowed() {
  try {
    const consent = JSON.parse(localStorage.getItem('cookieConsent'));
    return !!(consent && consent.accepted);
  } catch {
    return false;
  }
}

const CookieConsent = ({ onConsent }) => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [minimized, setMinimized] = useState(false);

  const messages = {
    pt: {
      text: "Este site utiliza cookies essenciais para garantir o funcionamento básico da navegação. Utilizamos cookies opcionais para guardar preferências de idioma, filtros de pesquisa e para fins estatísticos anónimos. Pode aceitar ou rejeitar estes cookies opcionais. Se rejeitar, as suas preferências não serão guardadas e as visitas não serão contabilizadas. Estas opções não afetam utilizadores autenticados (colaboradores ou proprietários).",
      more: "/politica-de-cookies",
      accept: "Aceitar cookies opcionais",
      reject: "Rejeitar cookies opcionais",
      manage: "Gerir preferências",
      save: "Guardar configurações"
    },
    en: {
      text: "This website uses essential cookies for basic navigation. We use optional cookies to save language preferences, search filters, and for anonymous statistics. You can accept or reject these optional cookies. If you reject, your preferences will not be saved and visits will not be tracked. These options do not affect authenticated users (employees or owners).",
      more: "/en/cookie-policy",
      accept: "Accept optional cookies",
      reject: "Reject optional cookies",
      manage: "Manage preferences",
      save: "Save settings"
    },
    fr: {
      text: "Ce site utilise des cookies essentiels pour assurer la navigation de base. Nous utilisons des cookies optionnels pour enregistrer les préférences de langue, les filtres de recherche et pour des statistiques anonymes. Vous pouvez accepter ou refuser ces cookies optionnels. Si vous refusez, vos préférences ne seront pas enregistrées et les visites ne seront pas comptabilisées. Ces options n'affectent pas les utilisateurs authentifiés (collaborateurs ou propriétaires).",
      more: "/fr/politique-de-cookies",
      accept: "Accepter les cookies optionnels",
      reject: "Refuser les cookies optionnels",
      manage: "Gérer les préférences",
      save: "Enregistrer les paramètres"
    }
  };

  const getLang = () => {
    const lang = document.documentElement.lang || navigator.language || 'pt';
    if (lang.startsWith('en')) return 'en';
    if (lang.startsWith('fr')) return 'fr';
    return 'pt';
  };

  const lang = getLang();
  const { text, more, accept, reject, manage, save } = messages[lang];

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setVisible(true);
    } else {
      const parsed = JSON.parse(consent);
      setConsentGiven(parsed.accepted);
      if (parsed.accepted && onConsent) {
        onConsent();
      }
    }
  }, [onConsent]);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ accepted: true, date: Date.now() }));
    setVisible(false);
    setConsentGiven(true);
    if (onConsent) {
      onConsent();
    }
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({ accepted: false, date: Date.now() }));
    setVisible(false);
    setConsentGiven(false);
    // Do not call onConsent, so optional cookies are not enabled
  };

  const handleSavePreferences = () => {
    if (consentGiven && onConsent) {
      onConsent();
    }
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  if (minimized) {
    return (
      <div style={{
        position: 'fixed',
        left: 16,
        bottom: 16,
        zIndex: 9999,
      }}>
        <button
          onClick={() => setMinimized(false)}
          style={{
            background: '#FFD700',
            color: '#111',
            border: 'none',
            borderRadius: 20,
            padding: '8px 18px',
            fontWeight: 500,
            fontSize: '1rem',
            cursor: 'pointer',
            boxShadow: '0 1px 4px #FFD70033'
          }}
          aria-label="Mostrar informações de cookies"
        >
          🍪 Cookies
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      width: 'auto',
      display: 'flex',
      justifyContent: 'center',
      zIndex: 9999,
      pointerEvents: 'auto'
    }}>
      <div style={{
        background: '#111',
        color: '#FFD700',
        borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
        padding: '20px 32px 16px 32px',
        maxWidth: 420,
        minWidth: 280,
        margin: '16px',
        textAlign: 'left',
        fontSize: '1rem',
        pointerEvents: 'auto',
        border: '1px solid #FFD700',
        position: 'relative'
      }}>
        <button
          onClick={() => setMinimized(true)}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: 'transparent',
            color: '#FFD700',
            border: 'none',
            fontSize: '1.2rem',
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
          aria-label="Minimizar banner de cookies"
          title="Minimizar"
        >
          &minus;
        </button>
        {!showSettings ? (
          <>
            <div style={{ marginBottom: 8, fontWeight: 500, fontSize: '1.08rem', lineHeight: 1.4 }}>
              {text}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <button
                onClick={handleAccept}
                style={{
                  background: '#FFD700',
                  color: '#111',
                  border: 'none',
                  borderRadius: 6,
                  padding: '7px 18px',
                  fontWeight: 500,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  boxShadow: '0 1px 4px #FFD70033'
                }}
              >
                {accept}
              </button>
              <button
                onClick={handleReject}
                style={{
                  background: '#111',
                  color: '#FFD700',
                  border: '1px solid #FFD700',
                  borderRadius: 6,
                  padding: '7px 18px',
                  fontWeight: 500,
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                {reject}
              </button>
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  background: '#222',
                  color: '#FFD700',
                  border: '1px solid #FFD700',
                  borderRadius: 6,
                  padding: '7px 18px',
                  fontWeight: 500,
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                {manage}
              </button>
            </div>
            <a
              href={more}
              style={{
                color: '#FFD700',
                textDecoration: 'underline',
                fontSize: '0.97rem',
                marginTop: 2,
                display: 'inline-block'
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {more}
            </a>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 8, fontWeight: 500, fontSize: '1.08rem', lineHeight: 1.4 }}>
              {text}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: '1rem', color: '#FFD700', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  style={{ accentColor: '#FFD700', width: 18, height: 18 }}
                />
                Cookies opcionais (preferências e estatísticas)
              </label>
            </div>
            <button
              onClick={handleSavePreferences}
              style={{
                background: '#FFD700',
                color: '#111',
                border: 'none',
                borderRadius: 6,
                padding: '7px 18px',
                fontWeight: 500,
                fontSize: '1rem',
                cursor: 'pointer',
                boxShadow: '0 1px 4px #FFD70033'
              }}
            >
              {save}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
